import type { Timestamp } from 'firebase/firestore';
import type {
  TheaWebOccasionEnum,
  TheaWebRecommendationModeEnum,
  TheaWebRecommendationStatusEnum,
} from './enums';
import type { RecipientSnapshot } from './recipient';

// Snapshot of a product as written by the recommendation pipeline.
// Mirrors carousel_agent._serialize_product_for_firestore (BE-side trimmer).
export interface RecommendationProduct {
  id: string;
  title: string;
  price: number;
  brand?: string;
  images?: string[];
  // CDN variants for fast first paint (populated by nightly backfill).
  images_cdn?: string[];
  images_cdn_mobile?: string[];
  gift_gender?: string;
  interests?: string[];
  description?: string;
  // Short, voice-preserving gift blurb (catalog `display_description`, mirrors
  // thea-shared-schemas product.json #10). Preferred over `description` for the
  // quick-view; falls back to a cleaned form of `description` when absent.
  display_description?: string;
  url?: string;
  // Sovrn affiliate redirect URL (bug #30). Populated by the on_product_written
  // trigger + weekly safety-net backfill in thea-serverless. Optional — products
  // wrapped before the trigger deployed may still be missing this field, in
  // which case the buy-click falls back to plain `url`.
  affiliateUrl?: string;
  carousel_tags?: string[];
  // Catalog availability, mirrored onto "More like this" rail entries by the
  // nightly `theaWebRefreshRailSnapshots` sync (THE-468). Rails are frozen
  // snapshots, so without these a tile whose product went out of stock is
  // indistinguishable from a live one — 19.6% of prod rail tiles measured.
  //
  // ALL THREE ARE OPTIONAL AND MUST FAIL OPEN. No other producer of this shape
  // writes them: frozen carousel sessions, `giftActivity` save snapshots, and
  // any rail entry the nightly sync has not reached yet all leave them absent.
  // Absent means "showable"; only an explicit `false` (or `soft_delete: true`)
  // hides anything. See `lib/railAvailability.ts` for the one predicate that
  // reads them.
  is_available?: boolean;
  is_published?: boolean;
  soft_delete?: boolean;
}

/**
 * Why a carousel finished with zero products. Written by the carousel agent
 * (thea-serverless `carousel_agent.py`, THE-556) onto
 * `carousels.{chip}.empty_reason` alongside `status: 'complete'` and
 * `product_count: 0`, and never written when the rail shipped products.
 *
 * Its presence is the ONLY trustworthy signal that the BACKEND came back empty.
 * "the rendered array is empty" is not the same question: the board's own price
 * slider, dismiss and save filters all empty a rail that arrived full, and those
 * still drop silently by design (THE-559 ask #3).
 */
export const CAROUSEL_EMPTY_REASONS = [
  /** Retrieval found nothing at all — a catalog/filter gap (THE-557). */
  'no_candidates',
  /** A candidate pool existed and the taste pass took none of it. */
  'curation_selected_zero',
  /** Every pick was already shown in a sibling rail. */
  'deduped_after_worker',
] as const;

/**
 * `'unknown'` is the forward-compatible bucket: a reason string this build has
 * never heard of still resolves to a rail that EXPLAINS ITSELF rather than one
 * that vanishes, which is the whole point of THE-559. A new backend reason
 * therefore degrades to the generic copy instead of regressing to the silent
 * drop this issue exists to remove.
 */
export type CarouselEmptyReason = (typeof CAROUSEL_EMPTY_REASONS)[number] | 'unknown';

export interface RecommendationCarousel {
  displayName: string;
  products: RecommendationProduct[];
  /** True while the agent is still generating this carousel (status not yet
   *  `complete`/`error`). Drives the per-carousel loading skeleton — e.g. a chip
   *  added in place shows a skeleton until its products stream in. */
  loading?: boolean;
  /** Set only when the agent finished this carousel with zero products. Absent
   *  on every rail that shipped products, and absent on every session written
   *  before THE-556 shipped — so absent means "no explicit empty state". */
  emptyReason?: CarouselEmptyReason;
}

// Frozen quiz snapshot at submit time.
export interface RecommendationInput {
  occasion: TheaWebOccasionEnum;
  occasionLabel?: string;
  interests: string[];
  freeform: string;
}

// Doc at `theaWebUser/{uid}/recipient/{recipientId}/recommendation/{ulid}`.
// One per generation. `isActive: true` for the current; flips to false on regenerate.
//
// Carousel data is NOT here — it lives in carouselSessions/{carouselSessionId}
// (written by the existing /feed agent). Subscribe to that doc separately for
// progressive paint.
export interface Recommendation {
  recommendationId: string;
  isActive: boolean;
  displayName?: string;
  input: RecommendationInput;
  recipientSnapshot: RecipientSnapshot;
  status: TheaWebRecommendationStatusEnum;
  mode: TheaWebRecommendationModeEnum;
  carouselSessionId: string;
  pipelineTimingMs?: number;
  errorMessage?: string;
  _mergedFrom?: string;
  /**
   * Ordered gift-guide keys matched to THIS recommendation's frozen inputs,
   * written by thea-serverless' match function (THE-436, SPEC §4d). Keys index
   * the PUBLISHED guide index in Firestore (`PublishedGuideListing[].key`,
   * which is the guide slug) — not the compiled-in registry, so a key whose
   * guide has since been unpublished simply resolves to nothing (THE-443).
   *
   * Per-RECOMMENDATION, not per-recipient, because a board renders one frozen
   * recommendation: a recipient-level result would reflect the newest quiz
   * inputs and could show beside an older recommendation built from different
   * age/gender. The full ranked gender/age-eligible list is stored here with
   * NO season prune and NO cap — the frontend owns both (see
   * resolveBoardGuideListings).
   *
   * Optional and frequently absent: every recommendation written before the
   * match function ships has none, which resolves to an empty rail.
   */
  guideMatches?: string[];
  _schemaVersion: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  completedAt?: Timestamp;
  archivedAt?: Timestamp;
}

// One vibe-worded hint bucket under a carousel — the BE's contract for
// THE-565, written to `carouselSessions/{id}.hints.{chipKey}[]`.
//
// The products it names are drawn from that carousel's `candidatePool`: the
// items the search surfaced but curation didn't put on the board. So a bucket
// is a promise the board can already keep — every id is a real product the
// client is permitted to read, and none of them are already on screen.
export interface CarouselHintBucket {
  /** `"h1"`, `"h2"`, … contiguous within a carousel. Stable within a session,
   *  NOT across sessions — safe as a telemetry label, never as a durable key. */
  id: string;
  /** Vibe-worded, ≤40 chars, e.g. "Cozy nights in". Written at board build
   *  because no product field carries a semantic label to derive one from. */
  label: string;
  /**
   * Exactly what "See more" renders — no resolution step, no client-side
   * filtering. Guaranteed by the BE to be ≥5 ids, all present in this
   * carousel's `candidatePool`, and none of them already on the board.
   */
  productIds: string[];
}

// Shape of carouselSessions/{id} after normalizeCarouselSession() runs. The
// agent writes snake_case to that doc; the munger maps to the camelCase shape
// the components consume so component code stays idiomatic.
export interface CarouselSession {
  status: TheaWebRecommendationStatusEnum;
  carousels: Record<string, RecommendationCarousel>;
  carouselOrder: string[];
  errorMessage?: string;
  pipelineTimingMs?: number;
  /**
   * Hint buckets keyed by chip key, present only once the BE's post-board
   * write lands (it runs AFTER `status: 'complete'` so board latency doesn't
   * regress — see THE-565). Three absences that are NOT the same thing and
   * must stay distinguishable:
   *
   *   - `hints` undefined         → the late write hasn't landed yet, or this
   *                                 is a pre-THE-565 session. Render nothing.
   *   - `hints[chip]` undefined   → this carousel produced no honest bucket
   *                                 (~1.6% of rows). Render nothing, forever.
   *   - `hints[chip]` = []        → should not occur; the BE omits the key
   *                                 rather than emitting an empty list.
   *
   * The first two are indistinguishable to a user and identical to render, but
   * they differ in whether waiting will help — which is why the normalizer is
   * careful never to collapse one into the other.
   */
  hints?: Record<string, CarouselHintBucket[]>;
}
